'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Copy,
  Facebook,
  Loader2,
  Mail,
  MessageCircle,
  QrCode,
  Share2,
  Twitter,
} from 'lucide-react';
import { useTRPC } from '@/lib/trpc/provider';
import { useAuthStore } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ShareButtonProps {
  /** Site-relative path of the content, e.g. `/courses/my-slug`. */
  path: string;
  /** Human-readable title, interpolated into the share text. */
  title: string;
  variant: 'course' | 'blog';
}

/**
 * Reusable public share affordance (DEC-1..DEC-8). Native OS share sheet (when
 * available) PLUS a dropdown of Copy link / Facebook / X / WhatsApp / Email /
 * WeChat QR. When a logged-in parent triggers a share, the URL carries their
 * referral code (`?ref={code}`) so public sharing feeds the existing referral /
 * credit loop; anonymous visitors and non-parents share the plain content URL.
 */
export function ShareButton({ path, title, variant }: ShareButtonProps) {
  const t = useTranslations('public.share');
  const trpc = useTRPC();

  const user = useAuthStore((s) => s.user);
  const hasHydrated = useAuthStore((s) => s.hasHydrated);
  const isParent = user?.role === 'parent';

  // Eager referral-code fetch (DEC-1, DEC-8): fires ONLY for confirmed parents,
  // once per page view — NOT gated on menu-open — so the code is already in
  // `data` before any share action, letting handlers build the URL synchronously
  // (critical for DEC-7b — no `await` before a user-activation-gated call).
  // Anonymous visitors / non-parents never call the query.
  const referralQuery = useQuery(
    trpc.referral.referralMyCode.queryOptions(undefined, {
      enabled: isParent,
      // The code never changes for a parent within a session — cache it so we
      // don't re-hit getOrCreateParentLink's SELECT on every course/blog page.
      staleTime: Infinity,
    })
  );
  const code = referralQuery.data?.code;

  // Readiness (F2 / Task 3a): key off `hasHydrated` — the ONLY reliable
  // "auth resolved" signal — NOT `isLoading` / `user !== undefined`. A parent
  // is ready only once their code has loaded; everyone else once hydration
  // completes. While not ready, the trigger is disabled — a parent can never
  // fall through to a bare (unattributed) URL while the fetch is in flight.
  // Escape hatch: if the code fetch has terminally FAILED (query settled with
  // an error after retries), fall back to a plain unattributed share rather
  // than leaving the trigger disabled forever (a permanent spinner is worse
  // than losing attribution on one visit).
  const ready =
    hasHydrated && (isParent ? !!code || referralQuery.isError : true);

  const [wechatOpen, setWechatOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  const text = t(variant === 'course' ? 'shareTextCourse' : 'shareTextBlog', { title });
  const subject = t(variant === 'course' ? 'emailSubjectCourse' : 'emailSubjectBlog');

  // Build the share URL SYNCHRONOUSLY at call time — `code` is preloaded so this
  // never awaits (DEC-7b). SSR never runs handlers, so `window` is safe here.
  const buildUrl = (): string => {
    const base = window.location.origin + path;
    return code ? `${base}?ref=${encodeURIComponent(code)}` : base;
  };

  const openIntent = (intentUrl: string) => {
    window.open(intentUrl, '_blank', 'noopener,noreferrer');
  };

  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const handleNativeShare = () => {
    const url = buildUrl();
    // No preceding `await` — preserves transient user activation (DEC-7b).
    void navigator.share({ title, text, url }).catch(() => {
      // User cancelled / share dismissed — not an error.
    });
  };

  const handleCopy = () => {
    // Guard clipboard existence up front; do NOT toast success unconditionally
    // (round-2 Finding 3 — optional-chaining a missing clipboard would still
    // short-circuit to a false "copied").
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      toast.error(t('copyFailed'));
      return;
    }
    const url = buildUrl();
    navigator.clipboard
      .writeText(url)
      .then(() => toast.success(t('copied')))
      .catch(() => toast.error(t('copyFailed')));
  };

  const handleFacebook = () => {
    const url = buildUrl();
    openIntent(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
  };

  const handleX = () => {
    const url = buildUrl();
    openIntent(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
    );
  };

  const handleWhatsApp = () => {
    const url = buildUrl();
    openIntent(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`);
  };

  const handleEmail = () => {
    const url = buildUrl();
    openIntent(
      `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${text} ${url}`)}`
    );
  };

  // Generate the WeChat QR AFTER the dialog opens (this `await` is safe — it runs
  // post-open, not before a share call — DEC-7b).
  useEffect(() => {
    if (!wechatOpen) return;
    let cancelled = false;
    setQrDataUrl(null);
    const base = window.location.origin + path;
    const url = code ? `${base}?ref=${encodeURIComponent(code)}` : base;
    void (async () => {
      try {
        const QRCode = await import('qrcode');
        const dataUrl = await QRCode.toDataURL(url, { width: 220, margin: 2 });
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch {
        if (!cancelled) setQrDataUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [wechatOpen, code, path]);

  const disabled = !ready;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            aria-busy={disabled}
            className="gap-2"
            data-testid="share-button-trigger"
          >
            {disabled ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Share2 className="h-4 w-4" />
            )}
            {t('button')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {/* Native share is ADDITIVE, not exclusive (DEC-7/F6) — the full
              channel list (incl. the WeChat QR) always stays reachable. */}
          {canNativeShare && (
            <DropdownMenuItem onSelect={handleNativeShare} data-testid="share-native">
              <Share2 className="h-4 w-4" />
              {t('button')}…
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={handleCopy} data-testid="share-copy">
            <Copy className="h-4 w-4" />
            {t('copyLink')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleFacebook} data-testid="share-facebook">
            <Facebook className="h-4 w-4" />
            {t('facebook')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleX} data-testid="share-x">
            <Twitter className="h-4 w-4" />
            {t('x')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleWhatsApp} data-testid="share-whatsapp">
            <MessageCircle className="h-4 w-4" />
            {t('whatsapp')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleEmail} data-testid="share-email">
            <Mail className="h-4 w-4" />
            {t('email')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setWechatOpen(true)} data-testid="share-wechat">
            <QrCode className="h-4 w-4" />
            {t('wechat')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={wechatOpen} onOpenChange={setWechatOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>{t('qrDialogTitle')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-3 py-2">
            {qrDataUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={qrDataUrl}
                alt=""
                className="h-[220px] w-[220px]"
                data-testid="share-wechat-qr"
              />
            ) : (
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            )}
            <p className="text-center text-sm text-muted-foreground">{t('wechatQrHint')}</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
